import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
// Imported here as well as in the builder: whichever of the two a
// person opens first has to bring the stylesheet, or the tiles stack up
// in the corner. The bundler emits it once.
import "leaflet/dist/leaflet.css";
import api from "../api";

/**
 * Which subgrids still need a spray route drawn (ROADMAP B6, Chris).
 *
 * The treatment builder's map, stripped to the two things this question
 * needs: the subgrids, and the surveillance sites inside them. Nothing here
 * edits anything -- the output is a list of places to go and draw.
 *
 * The framing that makes it useful is that the work is bounded. Routes are
 * only ever needed where we trap, and 1,563 of the district's 4,222 subgrids
 * hold an active site. Every one of those drawn once is the end of annual
 * route drawing, save for a new site in a new subgrid or a human case.
 */

interface Row {
    gridlabel: string;
    trs: string | null;
    depot: string | null;
    status: string | null;
    site_count: number;
    sites: string[];
    site_codes: string[];
    points: [number, number][];
    has_drawn: boolean;
    has_device: boolean;
    /** What this subgrid needs: nothing, a trace of the device track, or drawing. */
    action: "done" | "trace" | "draw";
}

interface Totals {
    subgrids_total: number;
    subgrids_with_sites: number;
    subgrids_to_draw: number;
    subgrids_to_trace: number;
    subgrids_done: number;
    sites_placed: number;
    sites_outside_any_subgrid: number;
    layers_configured: { drawn: boolean; device: boolean };
}

interface Payload {
    totals: Totals;
    rows: Row[];
    orphans: [string, string][];
    layer_errors: Record<string, string>;
    cached: boolean;
}

/** The subgrid polygons, for the map. Same layer the builder draws. */
const SUBGRID_LAYER_ID = "38daae7de0f441379614fb5446908958";

const STYLES = {
    draw: { color: "#b45309", weight: 1, fillColor: "#f59e0b", fillOpacity: 0.3 },
    trace: { color: "#1d4ed8", weight: 1, fillColor: "#60a5fa", fillOpacity: 0.25 },
    done: { color: "#15803d", weight: 1, fillColor: "#22c55e", fillOpacity: 0.18 },
} as const;

export default function SprayRoutesCard() {
    const [data, setData] = useState<Payload | null>(null);
    const [busy, setBusy] = useState<"" | "load" | "map">("");
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [depot, setDepot] = useState("");
    const [onlyOutstanding, setOnlyOutstanding] = useState(true);
    const [mapOpen, setMapOpen] = useState(false);

    const mapHost = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);

    const load = async (refresh = false) => {
        setBusy("load");
        setError(null);
        try {
            const r = await api.get(`/spray-route-coverage${refresh ? "?refresh=true" : ""}`);
            setData(r.data);
        } catch (e: any) {
            setError(
                e?.response?.data?.message ||
                "Could not work out coverage. This reads VectorSurv as well as AGOL, so a lapsed Gateway credential will stop it."
            );
        } finally {
            setBusy("");
        }
    };

    const depots = useMemo(
        () => Array.from(new Set((data?.rows ?? []).map((r) => r.depot).filter(Boolean))).sort() as string[],
        [data]
    );

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return (data?.rows ?? []).filter((r) => {
            if (onlyOutstanding && r.action === "done") return false;
            if (depot && r.depot !== depot) return false;
            if (!needle) return true;
            return (
                (r.gridlabel || "").toLowerCase().includes(needle) ||
                (r.trs || "").toLowerCase().includes(needle) ||
                r.sites.some((s) => s.toLowerCase().includes(needle)) ||
                r.site_codes.some((s) => s.includes(needle))
            );
        });
    }, [data, search, depot, onlyOutstanding]);

    /** Chris's working list: one line per subgrid, with the sites in it. */
    const downloadCsv = () => {
        const header = ["GridLabel", "TRS", "Depot", "Sites", "Site codes", "Site names",
            "Drawn route", "Device track", "Action"];
        const quote = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lines = [header.map(quote).join(",")];
        rows.forEach((r) =>
            lines.push(
                [
                    r.gridlabel ?? "",
                    r.trs ?? "",
                    r.depot ?? "",
                    String(r.site_count),
                    r.site_codes.join(" "),
                    r.sites.join(" | "),
                    r.has_drawn ? "yes" : "no",
                    r.has_device ? "yes" : "no",
                    r.action,
                ]
                    .map(quote)
                    .join(",")
            )
        );
        const blob = new Blob([lines.join(String.fromCharCode(10))], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "spray_routes_to_draw.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    // The map is built once, when it is first opened: 4,222 polygons is a
    // real download, and most visits to this card want the list.
    useEffect(() => {
        if (!mapOpen || !mapHost.current || mapRef.current || !data) return;
        let cancelled = false;

        const build = async () => {
            setBusy("map");
            try {
                const map = L.map(mapHost.current as HTMLElement, { scrollWheelZoom: true });
                L.tileLayer(
                    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
                    { maxZoom: 19, attribution: "Esri" }
                ).addTo(map);
                mapRef.current = map;

                const res = await api.post("/get-layer-geojson", { layer_id: SUBGRID_LAYER_ID });
                if (cancelled) return;

                const byLabel = new Map(data.rows.map((r) => [r.gridlabel, r]));
                const shapes = L.geoJSON(res.data.geojson, {
                    // Only the trapped subgrids are drawn. The other 2,659 are
                    // not part of this question and would bury the ones that are.
                    filter: (f: any) => byLabel.has(f?.properties?.GridLabel),
                    style: (f: any) =>
                        STYLES[byLabel.get(f?.properties?.GridLabel)?.action ?? "draw"],
                    onEachFeature: (f: any, layer) => {
                        const row = byLabel.get(f?.properties?.GridLabel);
                        if (!row) return;
                        layer.bindPopup(
                            `<strong>${row.gridlabel}</strong><br/>${row.depot ?? ""}<br/>` +
                            `${row.site_count} site${row.site_count === 1 ? "" : "s"}: ${row.sites.join(", ")}<br/>` +
                            `${row.action === "done" ? "Route drawn"
                                : row.action === "trace" ? "Device track only - trace it"
                                    : "<b>No route at all - draw it</b>"}`
                        );
                    },
                }).addTo(map);

                data.rows.forEach((r) =>
                    r.points.forEach(([lng, lat]) =>
                        L.circleMarker([lat, lng], {
                            radius: 3,
                            color: "#1d4ed8",
                            weight: 1,
                            fillColor: "#3b82f6",
                            fillOpacity: 0.9,
                        })
                            .bindTooltip(r.sites.join(", "))
                            .addTo(map)
                    )
                );

                const bounds = shapes.getBounds();
                if (bounds.isValid()) map.fitBounds(bounds);
                else map.setView([36.75, -119.77], 10);
            } catch (e: any) {
                setError(e?.response?.data?.message || "Could not load the subgrid shapes.");
            } finally {
                if (!cancelled) setBusy("");
            }
        };

        build();
        return () => {
            cancelled = true;
        };
    }, [mapOpen, data]);

    useEffect(
        () => () => {
            mapRef.current?.remove();
            mapRef.current = null;
        },
        []
    );

    const t = data?.totals;

    return (
        <div className="space-y-4">
            <p className="text-ink-soft text-[13px]">
                Spray routes are only ever needed where we trap, so this lists the subgrids
                holding a surveillance site and says which still need a route drawn. Print the
                list, draw those once, and what remains is a new site in a new subgrid or a
                confidential human case.
            </p>

            <div className="flex flex-wrap gap-2 items-center">
                <button onClick={() => load(false)} disabled={!!busy} className="btn-primary text-sm">
                    {busy === "load" ? "Working it out…" : data ? "Reload" : "Work out what's left"}
                </button>
                {data && (
                    <button onClick={() => load(true)} disabled={!!busy} className="btn-ghost text-sm">
                        Recheck from source
                    </button>
                )}
                {data?.cached && <span className="pill bg-paper-2 text-ink-faint">from cache</span>}
            </div>

            {busy === "load" && (
                <p className="text-[12px] text-ink-faint">
                    Reading 4,222 subgrids from AGOL and every surveillance site from VectorSurv,
                    then working out which sites fall in which subgrid. About a minute.
                </p>
            )}

            {error && <div className="p-3 rounded-sm2 bg-wnv-tint text-wnv text-[13px]">{error}</div>}

            {t && (
                <>
                    <div className="ui-card p-4">
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
                            <Stat label="To draw" value={t.subgrids_to_draw} strong />
                            <Stat label="To trace" value={t.subgrids_to_trace} />
                            <Stat label="Already drawn" value={t.subgrids_done} />
                            <Stat label="Subgrids with traps" value={t.subgrids_with_sites} />
                            <Stat label="Subgrids in total" value={t.subgrids_total} muted />
                            <Stat label="Sites placed" value={t.sites_placed} muted />
                        </div>
                        {(!t.layers_configured.drawn || !t.layers_configured.device) && (
                            <p className="text-[12px] text-ink-faint mt-3">
                                {!t.layers_configured.drawn && !t.layers_configured.device
                                    ? "Neither route layer is published yet, so every trapped subgrid reads as needing one."
                                    : !t.layers_configured.drawn
                                        ? "The drawn-routes layer is not published yet, so nothing can read as finished."
                                        : "The device-track layer is not published yet, so nothing can read as traceable."}{" "}
                                Set <code className="mx-1">PIONEER_SPRAY_ROUTE_DRAWN_ID</code> and
                                <code className="mx-1">PIONEER_SPRAY_ROUTE_DEVICE_ID</code> once they are.
                            </p>
                        )}
                        {Object.entries(data?.layer_errors ?? {}).map(([kind, message]) => (
                            <p key={kind} className="text-[12px] text-wnv mt-2">
                                The {kind} layer could not be read, so its subgrids are counted as
                                outstanding and this list is longer than the real work: {message}
                            </p>
                        ))}
                        {t.sites_outside_any_subgrid > 0 && (
                            <p className="text-[12px] text-wnv mt-2">
                                {t.sites_outside_any_subgrid} active site
                                {t.sites_outside_any_subgrid === 1 ? "" : "s"} fall outside every
                                subgrid, so no route can cover them. Either the point is wrong or the
                                mesh does not reach — worth a look either way.
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2 items-center">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Subgrid, TRS or site"
                            className="px-3 py-2 border border-line-strong rounded-sm2 text-[13px] min-w-[14rem]"
                        />
                        <select
                            value={depot}
                            onChange={(e) => setDepot(e.target.value)}
                            className="px-3 py-2 border border-line-strong rounded-sm2 text-[13px]"
                        >
                            <option value="">Every depot</option>
                            {depots.map((d) => (
                                <option key={d} value={d}>
                                    {d}
                                </option>
                            ))}
                        </select>
                        <label className="text-[13px] flex items-center gap-1.5">
                            <input
                                type="checkbox"
                                checked={onlyOutstanding}
                                onChange={(e) => setOnlyOutstanding(e.target.checked)}
                            />
                            Only ones still needing a route
                        </label>
                        <button onClick={downloadCsv} className="btn-ghost text-sm ml-auto">
                            Download this list ({rows.length})
                        </button>
                        <button onClick={() => setMapOpen((v) => !v)} className="btn-ghost text-sm">
                            {mapOpen ? "Hide map" : "Show map"}
                        </button>
                    </div>

                    {mapOpen && (
                        <div className="ui-card p-0 overflow-hidden">
                            <div ref={mapHost} className="h-[32rem] w-full" />
                            <p className="text-[12px] text-ink-faint p-3">
                                Amber needs a route drawn from scratch, blue has a device
                                track to trace, green already has a drawn route. The small dots
                                are surveillance sites. Only subgrids holding a site are shown.
                                {busy === "map" && " Loading the shapes…"}
                            </p>
                        </div>
                    )}

                    <div className="ui-card p-0 overflow-hidden">
                        <div className="overflow-auto max-h-[32rem]">
                            <table className="w-full text-[13px]">
                                <thead className="bg-paper-2 sticky top-0">
                                    <tr>
                                        {["Subgrid", "TRS", "Depot", "Sites", "Which sites", "Route"].map((h) => (
                                            <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.gridlabel} className="border-t border-line">
                                            <td className="px-3 py-2 font-mono text-[12px]">{r.gridlabel}</td>
                                            <td className="px-3 py-2 text-ink-soft">{r.trs ?? "—"}</td>
                                            <td className="px-3 py-2 text-ink-soft">{r.depot ?? "—"}</td>
                                            <td className="px-3 py-2">{r.site_count}</td>
                                            <td className="px-3 py-2 text-ink-soft max-w-[22rem] truncate" title={r.sites.join(", ")}>
                                                {r.sites.join(", ")}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {r.action === "done" ? (
                                                    <span className="pill bg-brand-tint text-brand-deep">Drawn</span>
                                                ) : r.action === "trace" ? (
                                                    <span className="pill bg-paper-2 text-ink-soft">Trace track</span>
                                                ) : (
                                                    <span className="pill bg-sle-tint text-sle">Draw</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {rows.length === 0 && (
                            <p className="p-4 text-[13px] text-ink-faint">Nothing matches that.</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function Stat({ label, value, strong, muted }: { label: string; value: number; strong?: boolean; muted?: boolean }) {
    return (
        <div>
            <div
                className={
                    strong
                        ? "font-extrabold text-[22px] text-sle"
                        : muted
                            ? "font-semibold text-[18px] text-ink-faint"
                            : "font-extrabold text-[22px]"
                }
            >
                {value.toLocaleString()}
            </div>
            <div className="eyebrow">{label}</div>
        </div>
    );
}
